/**
 * @file Employment.jsx
 * @description Manages new user setup and profile creation
 * 
 * @section Responsibilities
 * - Collect user information
 * - Handle document uploads
 * - Validate profile data
 * - Track onboarding progress
 * 
 * @section Dependencies
 * - Profile Management: User data handling
 * - Document Service: File handling
 * - Validation Service: Data validation
 * 
 * @section Integration Points
 * - Profile API: Data submission
 * - Document Service: File handling
 * - Onboarding Flow: Progress tracking
 * 
 * @section State Management
 * - profileData: User information
 * - validationState: Form validation
 * - progress: Onboarding progress
 * 
 * @section Events
 * - onProfileUpdate: Profile changes
 * - onDocumentUpload: File handling
 * - onValidation: Form validation
 * 
 * @section Props
 * @prop {Object} initialData - Initial profile data
 * @prop {Function} onComplete - Completion callback
 */

import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import TextInput from "../../../../components/forms/TextInput";
import SelectInput from "../../../../components/forms/SelectInput";

function Employment({ sendData }) {
  const statesArray = [
    { text: "AL", value: "AL" },
    { text: "AK", value: "AK" },
    { text: "AZ", value: "AZ" },
    { text: "AR", value: "AR" },
    { text: "AS", value: "AS" },
    { text: "CA", value: "CA" },
    { text: "CO", value: "CO" },
    { text: "CT", value: "CT" },
    { text: "DE", value: "DE" },
    { text: "DC", value: "DC" },
    { text: "FL", value: "FL" },
    { text: "GA", value: "GA" },
    { text: "GU", value: "GU" },
    { text: "HI", value: "HI" },
    { text: "ID", value: "ID" },
    { text: "IL", value: "IL" },
    { text: "IN", value: "IN" },
    { text: "IA", value: "IA" },
    { text: "KS", value: "KS" },
    { text: "KY", value: "KY" },
    { text: "LA", value: "LA" },
    { text: "ME", value: "ME" },
    { text: "MD", value: "MD" },
    { text: "MA", value: "MA" },
    { text: "MI", value: "MI" },
    { text: "MN", value: "MN" },
    { text: "MS", value: "MS" },
    { text: "MO", value: "MO" },
    { text: "MT", value: "MT" },
    { text: "NE", value: "NE" },
    { text: "NV", value: "NV" },
    { text: "NH", value: "NH" },
    { text: "NJ", value: "NJ" },
    { text: "NM", value: "NM" },
    { text: "NY", value: "NY" },
    { text: "NC", value: "NC" },
    { text: "ND", value: "ND" },
    { text: "MP", value: "MP" },
    { text: "OH", value: "OH" },
    { text: "OK", value: "OK" },
    { text: "OR", value: "OR" },
    { text: "PA", value: "PA" },
    { text: "PR", value: "PR" },
    { text: "RI", value: "RI" },
    { text: "SC", value: "SC" },
    { text: "SD", value: "SD" },
    { text: "TN", value: "TN" },
    { text: "TX", value: "TX" },
    { text: "TT", value: "TT" },
    { text: "UT", value: "UT" },
    { text: "VT", value: "VT" },
    { text: "VA", value: "VA" },
    { text: "VI", value: "VI" },
    { text: "WA", value: "WA" },
    { text: "WV", value: "WV" },
    { text: "WI", value: "WI" },
    { text: "WY", value: "WY" },
  ];

  const employmentStatusArray = [
    { text: "Self Employed", value: "SELF_EMPLOYED" },
    { text: "Employed", value: "EMPLOYED" },
    { text: "Retired", value: "RETIRED" },
    { text: "Student", value: "STUDENT" },
    { text: "Unemployed", value: "UNEMPLOYED" },
  ];

  const emptyData = {
    employmentStatus: "",
    employer: "",
    businessAddress: {
      streetAddress: [""],
      city: "",
      state: "",
      postalCode: "",
      country: "USA",
    },
  };

  const [data, setData] = useState(emptyData);

  const handleChange = (field, value) => {
    setData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleNestedChange = (field, subField, value) => {
    setData((prevState) => ({
      ...prevState,
      [field]: {
        ...prevState[field],
        [subField]: value,
      },
    }));
  };

  const handleArrayNestedInsert = (field, subField, value) => {
    setData((prevState) => ({
      ...prevState,
      [field]: {
        ...prevState[field],
        [subField]: [value],
      },
    }));
  };

  useEffect(() => {
    sendData(data);
  }, [data]);

  return (
    <>
      <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2, md: 12 }}>
        <Grid item xs={6} sm={6}>
          <div className="form-field">
            <SelectInput
              customInput={{
                label: "Employment Status",
                name: "employmentStatus",
                value: data.employmentStatus,
                action: (e) => handleChange("employmentStatus", e.target.value),
                tooltipBool: false,
              }}
              data={employmentStatusArray}
            ></SelectInput>
          </div>
        </Grid>
        <Grid item xs={6} sm={6}>
          <div className="form-field">
            <TextInput
              customInput={{
                label: "Employer",
                name: "employer",
                value: data.employer,
                action: (e) => handleChange("employer", e.target.value),
                tooltipBool: false,
                // disabled: data.employmentStatus != "EMPLOYED" ? true : false
              }}
            ></TextInput>
          </div>
        </Grid>
      </Grid>
      <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2, md: 12 }}>
        <Grid item xs={4} sm={4}>
          <div className="form-field">
            <SelectInput
              customInput={{
                label: "Country",
                name: "country",
                value: data.businessAddress.country,
                action: (e) =>
                  handleNestedChange(
                    "businessAddress",
                    "country",
                    e.target.value
                  ),
                tooltipBool: false,
                // disabled: true
              }}
              data={[{ text: "USA", value: "USA" }]}
            ></SelectInput>
          </div>
        </Grid>
        <Grid item xs={4} sm={4}>
          <div className="form-field">
            <SelectInput
              customInput={{
                label: "State",
                name: "state",
                value: data.businessAddress.state,
                action: (e) =>
                  handleNestedChange(
                    "businessAddress",
                    "state",
                    e.target.value
                  ),
                tooltipBool: false,
                // disabled: data.employmentStatus != "EMPLOYED" ? true : false
              }}
              data={statesArray}
            ></SelectInput>
          </div>
        </Grid>
        <Grid item xs={4} sm={4}>
          <div className="form-field">
            <TextInput
              customInput={{
                label: "City",
                name: "city",
                value: data.businessAddress.city,
                action: (e) =>
                  handleNestedChange("businessAddress", "city", e.target.value),
                tooltipBool: false,
                // disabled: data.employmentStatus != "EMPLOYED" ? true : false
              }}
            />
          </div>
        </Grid>
      </Grid>
      <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2, md: 12 }}>
        <Grid item xs={8} sm={8}>
          <div className="form-field">
            <TextInput
              customInput={{
                label: "Street Address",
                name: "streetAddress",
                value: data.businessAddress.streetAddress,
                action: (e) =>
                  handleArrayNestedInsert(
                    "businessAddress",
                    "streetAddress",
                    e.target.value
                  ),
                tooltipBool: false,
                maxLength: 30,
                // disabled: data.employmentStatus != "EMPLOYED" ? true : false
              }}
            />
          </div>
        </Grid>
        <Grid item xs={4} sm={4}>
          <div className="form-field">
            <TextInput
              customInput={{
                label: "Postal Code",
                name: "postalCode",
                value: data.businessAddress.postalCode,
                action: (e) =>
                  handleNestedChange(
                    "businessAddress",
                    "postalCode",
                    e.target.value
                  ),
                tooltipBool: false,
                // disabled: data.employmentStatus != "EMPLOYED" ? true : false
              }}
            ></TextInput>
          </div>
        </Grid>
      </Grid>
    </>
  );
}

export default Employment;
